import { CdkDrag, CdkDragDrop, CdkDragEnd, CdkDragHandle, CdkDragStart, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, EventEmitter, inject, Input, OnInit, Output, Renderer2, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { CountableTimeZonePipe, FS_TABLE_FORMATS, FS_TABLE_ROW_ACTIONS, FS_TABLE_ROW_DEFAULT, FS_TABLE_ROWS } from 'countable@helpers';
import { COLUMN_TYPES, DIALOG_MESSAGES, DIALOG_TYPES, DialogScheduleModel, EngagementScheduleModel, InputOutputEmitter, ROW_TYPES, SelectedIndexScheduleModel, SPIN_STYLES, SpinInputModel, TrialBalanceScheduleModel, UpdateEngagementScheduleModel, UpdateFormatScheduleModel, UpdateRowScheduleModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CoreModule } from '../../../../../core/core.module';
import { FormattedNumberPipe } from '../../../../../helpers/schedule/formatted-number.pipe';
import { ScheduleUtil } from '../../../../../helpers/schedule/schedule-util';
import { EngApiService } from '../../../../../services/eng-api.service';
import { ScheduleService } from '../../../../../services/schedule.service';
import { SchedulesDialogComponent } from '../../../../remote-shared/schedules/schedules-dialog/schedules-dialog.component';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-view-schedule',
  imports: [FormsModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatButtonModule, CommonModule, MatMenuModule,
    MatDialogModule, CountableTimeZonePipe, DragDropModule, CdkDropList, CdkDrag, CdkDragHandle, CoreModule, NgOptimizedImage, SharedModule, FormattedNumberPipe],
  templateUrl: './view-schedule.component.html',
  styleUrl: './view-schedule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:[ FormattedNumberPipe]
})
export class ViewScheduleComponent implements OnInit{
  protected readonly DIALOG_TYPES = DIALOG_TYPES;
  protected readonly InputOutputEmitter= InputOutputEmitter;
  protected readonly COLUMN_TYPES = COLUMN_TYPES;

  @Input() set refreshOrExpandEvent(value: InputOutputEmitter) {
    if(value){
      if(value === this.InputOutputEmitter.REFRESH){
        this.cancel();
        this.scheduleService.setRefresh(this.scheduleDetails().uuid);
      }else{
        this.onExpandOrCollapsed(value);
      }
    }
  }


  @Output() scheduleEvent: EventEmitter<InputOutputEmitter> = new EventEmitter();

  public formatList = structuredClone(FS_TABLE_FORMATS);
  public columnActions = structuredClone(FS_TABLE_ROW_DEFAULT);
  public rowKebabMenu = structuredClone(FS_TABLE_ROW_ACTIONS);
  public addRowMenu = structuredClone(FS_TABLE_ROWS);
  public rowTypes = ROW_TYPES;
  public scheduleDetails: WritableSignal<UpdateEngagementScheduleModel> = signal<UpdateEngagementScheduleModel>(new UpdateEngagementScheduleModel());
  public scheduleDetailsOriginal: WritableSignal<UpdateEngagementScheduleModel> = signal<UpdateEngagementScheduleModel>(new UpdateEngagementScheduleModel());
  public originalSchedules: WritableSignal<UpdateEngagementScheduleModel> = signal<UpdateEngagementScheduleModel>(new UpdateEngagementScheduleModel());
  public engagementDetails: WritableSignal<EngagementScheduleModel> = signal<EngagementScheduleModel>(new EngagementScheduleModel());
  public selectedFormatList: WritableSignal<typeof FS_TABLE_FORMATS> = signal<typeof FS_TABLE_FORMATS>([]);
  public selectedFormatListOriginal: WritableSignal<typeof FS_TABLE_FORMATS> = signal<typeof FS_TABLE_FORMATS>([]);
  public selectedIndex: WritableSignal<SelectedIndexScheduleModel> = signal<SelectedIndexScheduleModel>(new SelectedIndexScheduleModel());
  public filteredTrialBalData: WritableSignal<TrialBalanceScheduleModel[]> = signal<TrialBalanceScheduleModel[]>([]);
  public crossCheck: WritableSignal<any> = signal<any>(null);
  public groupIndex: WritableSignal<any> = signal<any>(null);
  public isAnyFormatApplied: WritableSignal<boolean> = signal<boolean>(false);
  public isColumnUpdated: WritableSignal<boolean> = signal<boolean>(false);
  public groupByRowsDeleted: WritableSignal<any> = signal<any>([]);
  public scheduleUtil: typeof ScheduleUtil = ScheduleUtil;
  private destroyRef = inject(DestroyRef);
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(null,SPIN_STYLES.LINE));
  spinCount = 0;
  public isSyncCall: WritableSignal<boolean> = signal<boolean>(false);

  totalValues = computed(() => {
    let total = {
      cyTotal: 0,
      pyTotal: 0,
    };

    this.scheduleDetails().schedules.forEach(schedule => {
      schedule.rows.forEach(row => {
        if (!(row.type === ROW_TYPES.SUBTOTAL) && !row.isDeleted) {
          total.cyTotal +=  (row.cyOriginalAmount || 0);
          total.pyTotal += (row.pyOriginalAmount || 0);
        }
      });
    });
    return total;
  });

  hasCYColumn = computed(()=>{
    return this.scheduleDetails().columns.some(column => column.type === this.COLUMN_TYPES.CY);
  });

  hasPYColumn = computed(()=>{
    return this.scheduleDetails().columns.some(column => column.type === this.COLUMN_TYPES.PY);
  });

  hasCYColumnClassName = computed(()=>{
    return this.scheduleDetails().columns.find(column => column.type === this.COLUMN_TYPES.CY)?.classNames;
  });

  hasPYColumnClassName = computed(()=>{
    return this.scheduleDetails().columns.find(column => column.type === this.COLUMN_TYPES.PY)?.classNames;
  });


  hasAnyRowChecked = computed(() => {
    return this.scheduleDetails().schedules.some(group => group.rows.some(row => row.isChecked));
  });

  hasAnySubtotalChecked = computed(() => {
    return this.scheduleDetails().schedules.some(group => group.rows.some(row => (row.isChecked && row.type === this.rowTypes.SUBTOTAL)));
  });

  isFormatEnabled = computed(() =>{
    return this.selectedFormatList().some(format => format.active);
  });

  isTotalHideDesc = computed(() => {
    const originalRows = this.scheduleDetailsOriginal().schedules?.flatMap(s => s.rows) ?? [];
    const updatedRows = this.scheduleDetails().schedules?.flatMap(s => s.rows) ?? [];
    const originalDeletedRows = this.scheduleDetailsOriginal().schedules?.flatMap(s => s.rows.filter(row => row.isDeleted)) ?? [];
    const updatedDeletedRows = this.scheduleDetails().schedules?.flatMap(s => s.rows.filter(row => row.isDeleted)) ?? [];
    return (
      (this.scheduleDetails().isTotalHide !== this.scheduleDetailsOriginal().isTotalHide) ||
      (this.scheduleDetails().isTotalDescHide !== this.scheduleDetailsOriginal().isTotalDescHide) ||
      (this.scheduleDetails().isCollapse !== this.scheduleDetailsOriginal().isCollapse) ||
      (originalRows.length !== updatedRows.length) || this.isColumnUpdated()
      || (originalDeletedRows.length !== updatedDeletedRows.length)
    );
  });

  isSaveEnabled = computed(()=>{
    const originalSchedules = structuredClone(this.scheduleDetailsOriginal().schedules);
    const updatedSchedules = structuredClone(this.scheduleDetails().schedules);

    const result = updatedSchedules.map((updatedSchedule) => {
      const originalSchedule = originalSchedules.find(
        (schedule) => schedule.id === updatedSchedule.id
      );

      if (originalSchedule) {
        const updatedRows = updatedSchedule.rows.map((updatedRow) => {
          const originalRow = originalSchedule.rows.find(
            (row) => row.id === updatedRow.id
          );

          if (originalRow) {
            const isAccountNameChanged = originalRow.accountNameChange !== updatedRow.accountNameChange;
            const isCyAmountChanged = this.scheduleUtil.convertToNumber(structuredClone(originalRow.cyOriginalAmount)) !== updatedRow.cyOriginalAmount;
            const isPyAmountChanged = this.scheduleUtil.convertToNumber(structuredClone(originalRow.pyOriginalAmount)) !== updatedRow.pyOriginalAmount;
            const isPositionChanged = originalRow.sortOrder !== updatedRow.sortOrder;
            const isBold = originalRow.isBold !== updatedRow.isBold;
            const isItalic = originalRow.isItalic !== updatedRow.isItalic;
            const isUnderLine = originalRow.isUnderLine !== updatedRow.isUnderLine;
            const isHide = originalRow.isHide !== updatedRow.isHide;

            return {
              ...updatedRow,
              isEdited: isAccountNameChanged || isCyAmountChanged || isPyAmountChanged || isPositionChanged || isBold || isItalic || isUnderLine || isHide,
            };
          }
          return { ...updatedRow, isEdited: true }; // Mark new rows as edited
        });
        return { ...updatedSchedule, rows: updatedRows };
      }
      return { ...updatedSchedule, rows: updatedSchedule.rows.map(row => ({ ...row, isEdited: !row.id })) };
    });

    return result.some(group => group.rows.some(row => row.isEdited));
  })

  saveChanges = computed(()=>{
    let data = {
      isDisable: false,
      rows: []
    }

    this.scheduleDetails().schedules.forEach((schedule, groupIndex) => {
      const editedRows = this.scheduleDetails().schedules[groupIndex].rows.filter(row => row.id);
      const updatedIds = editedRows.map(row => row.id);
      const filteredRows = structuredClone(this.scheduleDetailsOriginal().schedules[groupIndex].rows.filter(row => updatedIds.includes(row.id)));
      const updated = this.getUpdatedFields(structuredClone(filteredRows), editedRows);
      let groupLevel = {
        schId: schedule.id,
        rows: []
      };
      if (updated.length) {
        updated.sort((a, b) => a.id - b.id);
        updated.forEach((p: any) => {
          let obj = {
            id: p.id,
            tbId: p.trialBalanceId,
            type: p.type,
            ...(p.accountName !== undefined && p.accountName !== null && {accountNameChange: p.accountName}),
            ...(p.cyOriginalAmount !== undefined && p.cyOriginalAmount !== null && {cyAmountChange: p.cyOriginalAmount}),
            ...(p.pyOriginalAmount !== undefined && p.pyOriginalAmount !== null && {pyAmountChange: p.pyOriginalAmount}),
            ...(p.sortOrder !== undefined && p.sortOrder !== null && {sortOrder: p.sortOrder}),
            ...(p.isItalic !== undefined && p.isItalic !== null && {isItalic: p.isItalic}),
            ...(p.isBold !== undefined && p.isBold !== null && {isBold: p.isBold}),
            ...(p.isUnderLine !== undefined && p.isUnderLine !== null && {isUnderLine: p.isUnderLine}),
            ...(p.hasBottomLine !== undefined && p.hasBottomLine !== null && {hasBottomLine: p.hasBottomLine}),
            ...(p.hasTopLine !== undefined && p.hasTopLine !== null && {hasTopLine: p.hasTopLine})
          };
          const originalRow = filteredRows.filter(row => row.id === obj.id)[0];
          if (obj.type === this.rowTypes.SUBTOTAL) {
            obj.cyAmountChange = obj.hasOwnProperty('cyAmountChange') ? obj.cyAmountChange : originalRow.cyOriginalAmount;
            obj.pyAmountChange = obj.hasOwnProperty('pyAmountChange') ? obj.pyAmountChange : originalRow.pyOriginalAmount;
          }
          const currentRow = editedRows.find(row => row.id === obj.id);
          if(currentRow){
            obj.type = currentRow.type;
            obj.tbId = currentRow.trialBalanceId;
            obj.sortOrder = currentRow.sortOrder;
            obj.isBold = currentRow.isBold;
            obj.isItalic = currentRow.isItalic;
            obj.isUnderLine = currentRow.isUnderLine;
            obj.accountNameChange = currentRow.accountName;
            obj.cyAmountChange = obj.hasOwnProperty('cyAmountChange') ? obj.cyAmountChange : originalRow.cyOriginalAmount;
            obj.pyAmountChange = obj.hasOwnProperty('pyAmountChange') ? obj.pyAmountChange : originalRow.pyOriginalAmount;
          }
          groupLevel.rows.push(obj);
        });
      }
      const newRows = this.scheduleDetails().schedules[groupIndex].rows.filter(row => !row.id);
      if (newRows.length) {
        newRows.forEach(p => {
          groupLevel.rows.push({
            tbId: p.trialBalanceId,
            accountName: p.accountName ? p.accountName : '',
            cyAmount: p.cyOriginalAmount,
            pyAmount: p.pyOriginalAmount,
            sortOrder: p.sortOrder,
            isItalic: p.isItalic,
            isBold: p.isBold,
            isUnderLine: p.isUnderLine,
            hasBottomLine: p.hasBottomLine,
            hasTopLine: p.hasTopLine,
            type: p.type,
            isHide: p.isHide
          });
        });
      }
      groupLevel?.rows?.length ?  data.rows.push(groupLevel) : '';
    });
    data.isDisable = data.rows.length <= 0;
    return data;
  })

  constructor(private scheduleService: ScheduleService, public toasterService: ToastrService,
              private timeZonePipe: CountableTimeZonePipe, public dialog: MatDialog, private engApiService: EngApiService, private activatedRoute: ActivatedRoute,
              private renderer: Renderer2
  ) {

    effect(() => {
      this.engagementDetails.set(this.scheduleService.engagementScheduleSignal());
      this.scheduleService.saveChanges.next((!this.isSaveEnabled() || this.saveChanges().isDisable) && !this.isTotalHideDesc());
    });


    this.scheduleService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res=>{
      this.dialog?.closeAll();
      this.getScheduleDetails(res);
    })
  }

  ngOnInit(): void {
    this.addRowMenu.splice(this.addRowMenu.findIndex(m => m.key === 'subheader'), 1);
    this.columnActions.forEach(col =>{
      (col.key === 'delete') ?  col.subMenu = [] : '';
    });
    const list = this.formatList.filter(format => !(format.value === 'border-bottom' || format.value === 'border-top'));
    this.selectedFormatList.set(list);
    this.activatedRoute.queryParams.subscribe(param => {
      if(param?.NoteId){
        this.getScheduleDetails(param?.NoteId ? param?.NoteId : null);
      }
    });

    this.scheduleService.syncCallSpinner$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value=>{
      this.isSyncCall.set(value);
    });
  }

  private getScheduleDetails(viewId: string) {
    if(!viewId) return;
    const engagementData = JSON.parse(localStorage.getItem('selectedEngagementObj'));
    const payload = {
      'engagementId': engagementData?.engagementsid,
      'acctYear': +this.timeZonePipe.transform(engagementData?.yearEnd, 'yyyy', 'UTC'),
      'id': viewId
    };
    this.updateSpinnerConfig(true);
    this.scheduleService.getSchedule(payload).pipe(takeUntilDestroyed(this.destroyRef), finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200 && response.data) {
          let responseData = structuredClone(response.data);
          const filterDeletedRows = responseData?.schedules.map(group => {
            const filteredRows = group.rows
              .filter(row => row.isDeleted !== true)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((row, index) => ({ ...row, sortOrder: index + 1 }));

            return {
              ...group,
              rows: filteredRows
            };
          });

          responseData.schedules = filterDeletedRows;
          this.scheduleDetails.set(responseData);
          this.originalSchedules.set(structuredClone(responseData));
          this.scheduleDetails.update(schedule => {
            if(schedule.columns?.length){
              schedule.columns.sort((a, b) => a.type.localeCompare(b.type));
              schedule.columns.forEach(column => {
                column.yearEndName = column?.yearEndNameChange || column.yearEndName;
                this.updateEngagementSchedule({defaultDateFormat: this.scheduleUtil.getInputType(column.yearEndName)});
                if (column.isBold || column.isItalic || column.isUnderLine) {
                  column.classNames = [
                    column.isBold ? 'fw-semibold' : '',
                    column.isItalic ? 'text-italic overflow-visible' : '',
                    column.isUnderLine ? 'text-decoration-underline' : ''
                  ].join(' ');
                }
              });
            }else{
              schedule.columns = [];
            }
            schedule.schedules.forEach(group => {
              group.rows.sort((a, b) => a.sortOrder - b.sortOrder);
              group.rows.forEach(row => {

                row.accountName = !(row?.accountNameChange == null) ? row?.accountNameChange : row?.accountName;
                row.cyOriginalAmount = this.scheduleUtil.convertToNumber(!(row?.cyOriginalAmountChange == null) ? row?.cyOriginalAmountChange : row.cyOriginalAmount);
                row.pyOriginalAmount = this.scheduleUtil.convertToNumber(!(row?.pyOriginalAmountChange == null) ? row?.pyOriginalAmountChange : row.pyOriginalAmount);
                row.isChecked = false;
                row.isEdited = false;
                if (row.isBold || row.isItalic || row.isUnderLine) {
                  row.classNames = [
                    row.isBold ? 'fw-semibold' : '',
                    row.isItalic ? 'text-italic overflow-visible ' : '',
                    row.isUnderLine ? 'text-decoration-underline' : ''
                  ].join(' ');
                }
              });
            });
            schedule.disabled = true;
            return {...schedule}; // Ensure the updated schedule is returned
          });
          this.scheduleDetailsOriginal.set(structuredClone(this.scheduleDetails()));
          this.updateSubTotalValues();
          const isScheduleGroup = this.scheduleDetails().schedules.length > 1 ?
            (this.scheduleDetails().isCollapse ? this.InputOutputEmitter.COLLAPSED : this.InputOutputEmitter.EXPAND) : null;
          this.scheduleEvent.emit(isScheduleGroup);
          this.groupIndex.set(null);
        }else{
          response?.messages?.length ? this.scheduleService.handleMessage(response.messages) :
            this.scheduleService.handleMessage(response?.errors);
        }
        this.groupByRowsDeleted.set([]);
        this.isColumnUpdated.set(false);
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  public updateEngagementSchedule(engagementSchedule: Partial<SelectedIndexScheduleModel>): void {
    this.selectedIndex.update((current) => ({
      ...current,
      ...engagementSchedule
    }));
  }

  public resetSchedule() {
    const submoduleName = this.scheduleService.getEngagementSchedule()?.leftSideMenu?.find((module: any) => module.submoduleid == this.scheduleDetails().submodule )?.uirouting;
    submoduleName == 'balancesheet' ? this.getBalanceSheet() : this.getIncomeAndLoss() ;
  }

  //   column format
  public applyColumnFormat(selectedFormat: any, extendFormat: any) {
    if (selectedFormat.key === 'mmyyformat') {
      this.columnYearFormat(extendFormat);
    } else if (selectedFormat.key === 'format') {
      this.showFormatOptions();
    } else {
      this.deleteColumns();
    }
  }

  private columnYearFormat(extendFormat: any) {
    const formatType = (extendFormat.key === 'long') ? 'MMMM d, yyyy' : (extendFormat.key === 'short' ? 'MMM d, yyyy' : 'yyyy');
    const currentYear = this.timeZonePipe.transform(this.engagementDetails().engagementDetails.yearEnd, formatType, 'UTC');
    const previousYear = this.timeZonePipe.transform(this.engagementDetails().engagementDetails.yearEnd, formatType, 'UTC', true);
    let payload = [];
    this.scheduleDetails().columns.forEach(col => {
      payload.push({
        id: col.id,
        yearEndNameChange: col.type === 'CY' ? currentYear : previousYear
      });
    });
    this.updateColumns(payload);
  }

  private showFormatOptions() {
    if(this.selectedIndex().colIndex >= 0){
      const selectedData = this.scheduleDetails().columns[this.selectedIndex().colIndex];
      this.updateFormat(selectedData);
    } else {
      if(this.hasAnyRowChecked()){
        this.scheduleDetails().schedules.forEach(schedule =>{
          schedule.rows.forEach(row => {
            row.isChecked ? this.updateFormat(row) : '';
          })
        })
      } else {
        const selectedData = this.scheduleDetails().schedules[this.selectedIndex().groupIndex].rows[this.selectedIndex().rowIndex];
        this.updateFormat(selectedData);
      }
    }
  }

  protected updateFormat(selectedData: any){
    this.selectedFormatList.update(list => {
      const isAnySelected = !!selectedData.isItalic || !!selectedData.isBold || !!selectedData.isUnderLine;
      return list.map(c => ({
        ...c,
        active: true,
        isSelected: isAnySelected && (
          (c.value === 'italic' && !!selectedData.isItalic) ||
          (c.value === 'bold' && !!selectedData.isBold) ||
          (c.value === 'underline' && !!selectedData.isUnderLine)
        )
      }));
    });
    this.selectedFormatListOriginal.set(structuredClone(this.selectedFormatList()));
    this.findAnyFormatApplied();
  }

  protected findAnyFormatApplied(){
    let isFormatApplied = true;
    this.selectedFormatListOriginal().forEach((original: any) =>{
      this.selectedFormatList().forEach(updated =>{
        if(original.value === updated.value && isFormatApplied) {
          isFormatApplied = original.isSelected === updated.isSelected;
        }
      })
    });
    this.isAnyFormatApplied.set(isFormatApplied);
  }

  private updateColumns(payload: any[]) {
    this.updateSpinnerConfig(true);
    this.scheduleService.commonPutMethod(`/column`, payload).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.updateEngagementSchedule({defaultDateFormat: this.scheduleUtil.getInputType(payload[0].yearEndNameChange)});
          this.isColumnUpdated.set(true);
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  public selectedStyle(index: number) {
    this.selectedFormatList.update(list => {
      list[index].isSelected = !list[index].isSelected;
      return list;
    });
    this.findAnyFormatApplied();
  }

  public applyFormat() {
    const isItalicSelected = this.selectedFormatList().find(c => c.value === 'italic')?.isSelected;
    const isBoldSelected = this.selectedFormatList().find(c => c.value === 'bold')?.isSelected;
    const isUnderLineSelected = this.selectedFormatList().find(c => c.value === 'underline')?.isSelected;
    if (this.selectedIndex().colIndex >= 0 || this.selectedIndex().rowIndex >= 0) {
      const isRowLevel = this.selectedIndex().rowIndex >= 0;
      let multiFormatRowsOrCol: UpdateFormatScheduleModel[] = [];
      if(this.selectedIndex().colIndex >= 0){
        this.scheduleDetails.update(schedule => {
          schedule.columns[this.selectedIndex().colIndex] = {
            ...schedule.columns[this.selectedIndex().colIndex],
            isItalic: isItalicSelected ,
            isBold : isBoldSelected,
            isUnderLine : isUnderLineSelected,
            classNames: [
              isBoldSelected ? 'fw-semibold' : '',
              isItalicSelected ? 'text-italic overflow-visible' : '',
              isUnderLineSelected ? 'text-decoration-underline' : ''
            ].join(' ')
          }
          return {...schedule};
        });
        const selectedData = this.scheduleDetails().columns[this.selectedIndex().colIndex];
        multiFormatRowsOrCol.push({
          id: selectedData.id,
          hasTopLine: selectedData.hasTopLine,
          isItalic: selectedData.isItalic,
          isUnderLine: selectedData.isUnderLine,
          hasBottomLine: selectedData.hasBottomLine,
          isBold: selectedData.isBold
        });
      }else{
        if(this.hasAnyRowChecked()){
          this.scheduleDetails.update(groups =>{
            groups.schedules.forEach(schedule =>{
              if(schedule.rows.length){
                schedule.rows.sort((a, b) => a.sortOrder - b.sortOrder);
                schedule.rows.forEach(row=>{
                  if(row.isChecked){
                    row.isItalic = isItalicSelected;
                    row.isBold = isBoldSelected;
                    row.isUnderLine = isUnderLineSelected;
                    row.classNames = [
                      isBoldSelected? 'fw-semibold' : '',
                      isItalicSelected ? 'text-italic overflow-visible ' : '',
                      isUnderLineSelected ? 'text-decoration-underline' : ''
                    ].join(' ');
                  }
                })
              }
            })
            return {...groups};
          })
        }else{
          this.scheduleDetails.update(schedule => {
            const group = schedule.schedules[this.selectedIndex().groupIndex];
            group.rows[this.selectedIndex().rowIndex] = {
              ...group.rows[this.selectedIndex().rowIndex],
              isItalic: isItalicSelected ,
              isBold : isBoldSelected,
              isUnderLine : isUnderLineSelected,
              classNames : [
                isBoldSelected ? 'fw-semibold' : '',
                isItalicSelected ? 'text-italic overflow-visible ' : '',
                isUnderLineSelected ? 'text-decoration-underline' : ''
              ].join(' ')
            }
            return {...schedule};
          });
        }
      }
      if(isRowLevel){
        this.cancel();
      }else{
        this.saveFormatOptions(`/format?rowLevel=${isRowLevel}`, multiFormatRowsOrCol);
      }
    }
  }

  public cancel() {
    this.updateEngagementSchedule({groupIndex: -1, rowIndex: -1, colIndex: -1});
    const list = this.formatList.filter(format => !(format.value === 'border-bottom' || format.value === 'border-top'));
    this.selectedFormatList.set(list);
    this.scheduleDetails.update(groups => {
      groups.schedules.forEach(schedule => {
        schedule.rows.forEach(row => {
          row.isChecked = false;
        });
      });
      return { ...groups };
    });
    this.isAnyFormatApplied.set(false);
  }

  //   save row or column format options
  private saveFormatOptions(path: string, payload: UpdateFormatScheduleModel[]) {
    this.updateSpinnerConfig(true);
    this.scheduleService.updateFormat(path, payload).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.cancel();
          this.isColumnUpdated.set(true);
          // if((!this.isSaveEnabled() || this.saveChanges().isDisable) && !this.isTotalHideDesc()){
          //   this.scheduleService.setRefresh(this.scheduleDetails().uuid);
          // }
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  public rowActions(index: number) {
    switch (this.rowKebabMenu[index].key) {
      case 'hide':
        this.scheduleDetails.update(schedule => {
          const group = schedule.schedules[this.selectedIndex().groupIndex];
          group.rows[this.selectedIndex().rowIndex] = {
            ...group.rows[this.selectedIndex().rowIndex],
            isHide: !group.rows[this.selectedIndex().rowIndex].isHide
          }
          return {...schedule};
        });

        const currentRow = this.scheduleDetails().schedules[this.selectedIndex().groupIndex].rows[this.selectedIndex().rowIndex];
        if(currentRow.id){
          this.updateSpinnerConfig(true);
          this.scheduleService.commonPutMethod(`/row/status/${this.scheduleDetails().uuid}?isHide=${currentRow.isHide}`, [currentRow.id]).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
            next: response => {
              if (response.status === 200) {
                this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_UPDATED);
                this.cancel();
              }
            },
            error: e => {
              this.toasterService.error(e.error);
            }
          });
        }else{
          this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_UPDATED);
          this.cancel();
        }

        break;
      case 'format':
        this.showFormatOptions();
        break;
      case 'delete':
        this.deleteRows();
        break;
      case 'subrow':
        this.scheduleDetails.update(schedule => {
          const group = schedule.schedules[this.selectedIndex().groupIndex];
          let textOnlyRowIndex = this.selectedIndex().rowIndex + 1;
          let totalLength = group.rows.length + 1;
          while (textOnlyRowIndex < totalLength) {
            if (group.rows[textOnlyRowIndex] && group.rows[textOnlyRowIndex].type === this.rowTypes.SUBROW) {
              textOnlyRowIndex++;
            } else {
              const newRow: UpdateRowScheduleModel = new UpdateRowScheduleModel();
              newRow.type = this.rowTypes.SUBROW;
              newRow.sortOrder = 0;
              newRow.isEdited = true;
              group.rows.splice(textOnlyRowIndex, 0, newRow);
              textOnlyRowIndex = group.rows.length + 1;
            }
          }

          return {...schedule};
        });
        this.updateSubTotalValues();
        break;
    }
  }

  public onChangeCheckBox(event: MatCheckboxChange,groupIndex:number, index: number) {
    this.scheduleDetails.update(schedule => {
      const group = schedule.schedules[groupIndex];
      group.rows[index].isChecked = event.checked;
      return {...schedule};
    });
  }

  public addNewRow(index: number, subIndex: number, groupIndex: number) {
    const groupRowLength = structuredClone(this.scheduleDetails().schedules[groupIndex].rows.length);

    if (this.addRowMenu[index].key === 'subtotal' && this.scheduleDetails().schedules[groupIndex].rows[groupRowLength - 1].type === ROW_TYPES.SUBTOTAL) {
      this.toasterService.error('Cannot add a subtotal row after another subtotal row.');
      return;
    }
    const addRowLengthType = this.addRowMenu[index].key === 'subtotal' ? {length: 1, type: ROW_TYPES.SUBTOTAL} :
      (this.addRowMenu[index].key === 'textonly' ? {
          length: (this.addRowMenu[index].subMenu[subIndex].key === 0 ? 1 : (this.addRowMenu[index].subMenu[subIndex].key + 1)),
          type: ROW_TYPES.TEXT
        } :
        {length: this.addRowMenu[index].subMenu[subIndex].key, type: ROW_TYPES.GENERAL});
    const totalLength = (groupRowLength + addRowLengthType.length);
    let lastRowSortOrder = groupRowLength > 0 ? this.scheduleDetails().schedules[groupIndex].rows[groupRowLength - 1].sortOrder : 1;
    while (this.scheduleDetails().schedules[groupIndex].rows.length < totalLength) {
      const newRow: UpdateRowScheduleModel = new UpdateRowScheduleModel();
      newRow.type = addRowLengthType.type;
      newRow.sortOrder = lastRowSortOrder++;
      newRow.isEdited = true;
      if (addRowLengthType.type === this.rowTypes.SUBTOTAL) {
        newRow.isBold = true;
        newRow.accountName = 'Subtotal';
        newRow.accountNameChange = newRow.accountName;
        newRow.classNames = 'fw-semibold';
      }
      this.scheduleDetails().schedules[groupIndex].rows.push(newRow);
      if (addRowLengthType.type === this.rowTypes.TEXT && !(this.addRowMenu[index].subMenu[subIndex].key === 0)) {
        addRowLengthType.type = ROW_TYPES.SUBROW;
      }
    }
    this.updateSubTotalValues();
    this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_ADDED);
  }


  private updateRows(payload: any) {
    this.updateSpinnerConfig(true);
    this.scheduleService.updateRows(`/row`, payload).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.scheduleService.sendMessage({uuid: this.scheduleDetails().uuid});
          this.scheduleService.updatePreview(this.scheduleDetails().uuid);
          this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_UPDATED);
          this.getScheduleDetails(this.scheduleDetails().uuid);
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  public saveSchedule() {
    this.updateSubTotalValues();
    const rowIds = this.groupByRowsDeleted()?.flatMap((item: any) => item.rows.filter((row: any) => row.id !== null).map((row: any) => row.id));
    if(this.groupByRowsDeleted()?.length && rowIds?.length) {
      this.deleteColumnsRows(`/row`, rowIds, null)
    } else {
      let updatedRows: any[] = this.saveChanges().rows;
      if (updatedRows.length) {
        this.updateRows(updatedRows);
      } else {
        this.scheduleService.sendMessage({uuid: this.scheduleDetails().uuid});
        this.scheduleService.updatePreview(this.scheduleDetails().uuid);
        this.getScheduleDetails(this.scheduleDetails().uuid);
      }
    }
  }

  private getUpdatedFields(original: any[], updated: any[]): any[] {
    return updated.map((updatedObj, idx) => {
      const originalObj = original[idx];
      const changes: any = {};

      // Detect changed fields
      Object.keys(updatedObj).forEach(key => {
        if (updatedObj[key] !== originalObj[key]) {
          changes[key] = updatedObj[key];
        }
      });

      // Skip if only 'id' and 'isDeleted' are the changed keys
      const changedKeys = Object.keys(changes);
      const onlyIdAndIsDeleted =
        changedKeys.length === 2 &&
        changedKeys.includes('id') &&
        changedKeys.includes('isDeleted');

      if (changedKeys.length > 0 && !onlyIdAndIsDeleted) {
        changes.id = updatedObj.id; // Ensure id is included
        return changes;
      }

      return null;
    }).filter(Boolean);
  }

  public updateValues(event: any, keyName: string,groupIndex: number, index: number) {
    const target = event.target;
    const newValue = (target.type === 'number') ? Math.round(+target.value || 0) : target.value;
    this.scheduleDetails.update(schedule => {
      const group = schedule.schedules[groupIndex];
      group.rows[index] = {
        ...group.rows[index],
        [keyName]: newValue,
        [`${keyName}Change`]: newValue,
        isEdited: true
      };
      return {...schedule};
    });
    this.updateSubTotalValues();
  }

  private updateSubTotalValues() {
    let cySubtotal = 0;
    let pySubtotal = 0;
    let sortOrder = 1;
    this.scheduleDetails.update(schedule => {
      schedule.schedules.forEach(group => {
        cySubtotal = 0;
        pySubtotal = 0;
        sortOrder = 1;
        group.rows.forEach(row => {
          row.sortOrder = sortOrder++;
          if (row.type === 'SUBTOTAL') {
            row.cyOriginalAmount = this.scheduleUtil.convertToNumber(cySubtotal);
            row.pyOriginalAmount = this.scheduleUtil.convertToNumber(pySubtotal);

            cySubtotal = 0;
            pySubtotal = 0;
          } else if ((row.type === 'GENERAL' || row.type === 'SUB_ROW') && !row.isDeleted) {
            // Add amounts to the subtotal
            cySubtotal += row.cyOriginalAmount ? row.cyOriginalAmount : 0;
            pySubtotal += row.pyOriginalAmount ? row.pyOriginalAmount : 0;
          }
        });
      });

      return {...schedule};
    });
    this.crossCheckTotal();
  }

  //   drag and drop functionality can be implemented here if needed
  public onDrop(event: CdkDragDrop<any[]>, groupIndex: number) {
    const draggedRowIndex = event.previousIndex;
    const targetRowIndex = event.currentIndex;
    const filterRows = this.scheduleDetails().schedules[groupIndex].rows.filter(row => !row.isDeleted);
    const updatedRows = this.handleDragAndDrop(filterRows, draggedRowIndex, targetRowIndex, groupIndex);
    this.scheduleDetails.update(schedule => {
      const group = schedule.schedules[groupIndex];
      group.rows = updatedRows;
      return {...schedule};
    });
    this.updateSubTotalValues();
  }

  private handleDragAndDrop(rows: UpdateRowScheduleModel[], draggedRowIndex: number, targetRowIndex: number, groupIndex: number): UpdateRowScheduleModel[] {
    const draggedRow = rows[draggedRowIndex];
    const isDraggingTextWithSubrows = draggedRow.type === ROW_TYPES.TEXT;

    // Step 1: Identify
    let groupToMove: UpdateRowScheduleModel[] = [];
    if (isDraggingTextWithSubrows) {
      groupToMove.push(draggedRow);
      let nextIndex = draggedRowIndex + 1;
      while (rows[nextIndex] && rows[nextIndex].type === ROW_TYPES.SUBROW) {
        groupToMove.push(rows[nextIndex]);
        nextIndex++;
      }
    } else {
      groupToMove.push({...draggedRow}); // clone for safe type manipulation
    }

    const targetRow = rows[targetRowIndex];

    if (!isDraggingTextWithSubrows) {
      const single = groupToMove[0];

      // GENERAL becomes SUBROW if dropped into TEXT or SUBROW
      if (single.type === ROW_TYPES.GENERAL && (targetRow.type === ROW_TYPES.TEXT || targetRow.type === ROW_TYPES.SUBROW)) {
        single.type = ROW_TYPES.SUBROW;
      }

      // SUBROW becomes GENERAL if dropped outside TEXT/SUBROW
      if (single.type === ROW_TYPES.SUBROW && (targetRow.type !== ROW_TYPES.TEXT && targetRow.type !== ROW_TYPES.SUBROW)) {
        single.type = ROW_TYPES.GENERAL;
      }

      // TEXT and SUBTOTAL retain type (defensive)
      if (single.type === ROW_TYPES.TEXT || single.type === ROW_TYPES.SUBTOTAL) {
        single.type = rows[draggedRowIndex].type;
      }

      groupToMove[0] = single;
    }

    // Step 4: Remove the group
    const updatedRows = [...rows];
    updatedRows.splice(draggedRowIndex, groupToMove.length);

    // Adjust insert index when moving downward
    const insertIndex = draggedRowIndex < targetRowIndex ? targetRowIndex - groupToMove.length + 1 : targetRowIndex;

    // Step 5: Insert group
    updatedRows.splice(insertIndex, 0, ...groupToMove);

    let isValid = true;
    let isTextOnlyRow = false;
    updatedRows.forEach((row, index) => {
      isValid = (row.type === ROW_TYPES.SUBTOTAL && index === 0 && isValid) ? false : isValid;
      isValid = (row.type === ROW_TYPES.TEXT && updatedRows[index + 1] && updatedRows[index + 1].type === ROW_TYPES.SUBTOTAL && isValid) ? false : isValid;
      isValid = (row.type === ROW_TYPES.SUBTOTAL && updatedRows[index + 1] && updatedRows[index + 1].type === ROW_TYPES.SUBTOTAL && isValid) ? false : isValid;
      if(row.type === ROW_TYPES.TEXT){
        isTextOnlyRow = true;
      }else if(row.type === ROW_TYPES.SUBROW){
        row.type = isTextOnlyRow ? ROW_TYPES.SUBROW : ROW_TYPES.GENERAL;
      }else{
        isTextOnlyRow = false;
      }
    });

    return isValid ? updatedRows : structuredClone(this.scheduleDetails().schedules[groupIndex].rows);
  }

  public deleteSchedule(type: DIALOG_TYPES, restData?: any) {
    this.scheduleService.confirmDeleteSchedule(this.scheduleDetails(), type, restData);
  }

  private deleteColumns(all: boolean = false) {
    const schedule = new DialogScheduleModel();
    schedule.headerText = DIALOG_MESSAGES.HEADER_COLUMN_DELETE;
    schedule.subHeaderText = DIALOG_MESSAGES.SUB_HEADER_COLUMN_DELETE;
    schedule.subInfoText = DIALOG_MESSAGES.INFO_TEXT;
    schedule.buttonCancelText = DIALOG_MESSAGES.BUTTON_CANCEL_TEXT;
    schedule.buttonConfirmText = DIALOG_MESSAGES.BUTTON_CONFIRM_TEXT;
    schedule.type = DIALOG_TYPES.DELETE;
    const config: MatDialogConfig = new MatDialogConfig<any>();
    config.data = schedule;
    config.disableClose = true;
    const dialogRef = this.dialog.open(SchedulesDialogComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const columnList = all ? this.scheduleDetails().columns.map(c => c.id) : [this.scheduleDetails().columns[this.selectedIndex().colIndex].id];
        this.scheduleDetails.update(schedule => {
          const columnIdToRemove = this.scheduleDetails().columns[this.selectedIndex().colIndex]?.id;
          schedule.columns = schedule.columns.filter(column => column.id !== columnIdToRemove);
          return { ...schedule };
        });
        this.deleteColumnsRows(`/column`, columnList, DIALOG_MESSAGES.COLUMN_MESSAGE_DELETED);
      }
    });
  }

  protected getIncomeAndLoss() {
    let balanceSheetObj = {
      'engagementsid': this.engagementDetails().engagementId,
      'acctyear': this.engagementDetails().acctYear,
      'submoduleid': 436,
      'searchtext': ''
    };
    const groupLinkIds = this.scheduleDetails().schedules.map(schedule => schedule.groupLinkId);
    this.updateSpinnerConfig(true);
    this.isSyncCall.set(true);
    this.engApiService.getWFSIncomeStatement(JSON.stringify(balanceSheetObj)).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
      next: res => {
        this.isSyncCall.set(false);
        if (res.status === 200) {
          let data = JSON.parse(res?.data);
          let prev = data?.groups[0]['Prevgrouptotal'];
          const selectedGroups = this.scheduleUtil.getGroupsFromGroupLinkId(data.groups,groupLinkIds);
          if(selectedGroups?.length){
            this.deleteSchedule(DIALOG_TYPES.RESET, {subGroups: selectedGroups, prev: !!(prev || prev == 0)});
          }
        }
      },
      error: (error) => {
        this.toasterService.error(error.error);
        this.isSyncCall.set(false);
      }
    });
  }

  //   for resetting the schedule first call the balance sheet API
  private getBalanceSheet() {
    const balanceSheetObj = {
      'engagementsid': this.engagementDetails().engagementId,
      'acctyear': this.engagementDetails().acctYear,
      'searchtext': ''
    };
    this.updateSpinnerConfig(true);
    this.isSyncCall.set(true);
    const groupLinkIds = this.scheduleDetails().schedules.map(schedule => schedule.groupLinkId);
    this.engApiService.getWFSBalanceSheet(JSON.stringify(balanceSheetObj)).pipe(takeUntilDestroyed(this.destroyRef), finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: (response) => {
        this.isSyncCall.set(false);
        const data = JSON.parse(response?.data);
        let prev = data?.groups[0]['Prevgrouptotal'];
        const selectedGroups = this.scheduleUtil.getGroupsFromGroupLinkId(data.groups,groupLinkIds);
        if(selectedGroups?.length){
          this.deleteSchedule(DIALOG_TYPES.RESET, {subGroups: selectedGroups, prev: !!(prev || prev == 0)});
        }
      },
      error: (error) => {
        this.toasterService.error(error.error);
        this.isSyncCall.set(false);
      }
    });
  }

  private deleteRows() {
    const schedule = new DialogScheduleModel();
    schedule.headerText = DIALOG_MESSAGES.HEADER_ROW_DELETE;
    schedule.subInfoText = DIALOG_MESSAGES.INFO_TEXT;
    schedule.buttonCancelText = DIALOG_MESSAGES.BUTTON_CANCEL_TEXT;
    schedule.buttonConfirmText = DIALOG_MESSAGES.BUTTON_CONFIRM_TEXT;
    schedule.errorText = DIALOG_MESSAGES.ERROR_TEXT;
    schedule.type = DIALOG_TYPES.DELETE;
    this.scheduleDetails().schedules.forEach((schedule: any, groupIndex: number) =>{
      let row: any = null;
      if(groupIndex === this.selectedIndex().groupIndex){
        row = schedule.rows[this.selectedIndex().rowIndex];
      }
      const rowTypes = this.scheduleUtil.getRowType(structuredClone(schedule.rows), row, this.selectedIndex().rowIndex);
      if (rowTypes.length){
        const groupByRows = this.groupByRowsDeleted()?.length ? this.groupByRowsDeleted().filter((g: any) => g.id == schedule.id) : [];
        const rows = (groupByRows?.length ? groupByRows[0].rows : groupByRows);
        this.groupByRowsDeleted().push({
          id: schedule.id,
          rows: [...rows, ...rowTypes]
        });
      }
    })
    const isTextOnly = this.groupByRowsDeleted().some((group: any) => group.rows.some((row: any) => row.type === ROW_TYPES.TEXT));
    const isSubTotal = this.groupByRowsDeleted().some((group: any) => group.rows.some((row: any) => row.type === ROW_TYPES.SUBTOTAL));
    const isGeneral = this.groupByRowsDeleted().some((group: any) => group.rows.some((row: any) => (row.type === ROW_TYPES.GENERAL || row.type === ROW_TYPES.SUBROW)));
    schedule.subHeaderText = isTextOnly ? DIALOG_MESSAGES.SUB_HEADER_ROW_TEXT_DELETE : DIALOG_MESSAGES.SUB_HEADER_ROW_DELETE;
    const config: MatDialogConfig = new MatDialogConfig<any>();
    config.data = schedule;
    config.disableClose = true;
    if(!isGeneral && !isTextOnly && isSubTotal){
      this.deleteSubtotalRows();
    }else{
      this.dialog.open(SchedulesDialogComponent, config).afterClosed().subscribe(result => {
        if (result && isSubTotal) {
          this.deleteSubtotalRows();
        } else if (result) {
          this.deleteSelectedRows();
        }
      });
    }

  }

  private deleteSubtotalRows() {
   const config = this.scheduleUtil.getDeleteSubTotalConfig();
    this.dialog.open(SchedulesDialogComponent, config).afterClosed().subscribe(result => {
      if (result) {
        this.deleteSelectedRows();
      }
    });
  }

  private deleteSelectedRows(){
    this.scheduleDetails.update(schedule => {
      schedule.schedules.forEach(group => {
        const groupRow = this.groupByRowsDeleted().filter((g: any) => g.id === group.id);
        const sortOrders = groupRow.flatMap((item: any) => item.rows.map((row: any) => row.sortOrder));
        group.rows.filter(row => {
          if(sortOrders.includes(row.sortOrder)){
            row.isDeleted = true;
          }
        });
      });
      return { ...schedule };
    });
    this.updateSubTotalValues();
    this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_DELETED);
  }

  private deleteColumnsRows(path: string, list: number[], message: string) {
    if (list?.length) {
      this.updateSpinnerConfig(true);
      this.scheduleService.deleteColumnsOrRows(path, (path === '/column') ? list[0] : list).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
        next: response => {
          if (response.status === 200) {
            if(message){
              this.toasterService.success(message);
              this.isColumnUpdated.set(true);
            }else{
              this.groupByRowsDeleted.set([]);
              this.updateSubTotalValues();
              this.saveSchedule();
            }
          } else {
            response?.messages?.length ? this.scheduleService.handleMessage(response.messages) : this.scheduleService.handleMessage(response?.errors);
          }
        },
        error: e => {
          this.toasterService.error(e.error);
        }
      });
    } else {
      this.getScheduleDetails(this.scheduleDetails().uuid);
    }
  }

  public getTrialBalanceData(groupIndex: any) {
    if(this.groupIndex() === groupIndex){
      this.getGroupByFilter(groupIndex);
      return
    }
    const payload = {
      engId: this.engagementDetails().engagementId,
      acctYear: this.engagementDetails().acctYear,
      finSubgroupChildId: this.scheduleDetails().schedules[groupIndex].groupLinkId
    };
    this.scheduleService.getTrialBalanceData(payload).subscribe({
      next: response => {
        if (response.status === 200 && response.data) {
          this.filteredTrialBalData.set(response.data);
          this.getGroupByFilter(groupIndex);
          this.groupIndex.set(groupIndex);
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  public hideDescOrTotal(value: string) {
    this.scheduleDetails.update(schedule => {
      schedule.isTotalDescHide = value === 'DESC' ? !schedule.isTotalDescHide : schedule.isTotalDescHide;
      schedule.isTotalHide = value === 'HIDE' ? !schedule.isTotalHide : schedule.isTotalHide;
      return {...schedule};
    });
    this.updateSpinnerConfig(true);
    this.scheduleService.commonPutMethod(`/total/status/${this.scheduleDetails().uuid}?isTotalHide=${this.scheduleDetails().isTotalHide}&isDescHide=${this.scheduleDetails().isTotalDescHide}`, null)
      .pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_UPDATED);
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  protected onExpandOrCollapsed(value: InputOutputEmitter){
    this.scheduleDetails.update(schedule => {
      schedule.isCollapse = value === this.InputOutputEmitter.COLLAPSED;
      return {...schedule};
    });
    this.scheduleEvent.emit((this.scheduleDetails().isCollapse ? this.InputOutputEmitter.COLLAPSED : this.InputOutputEmitter.EXPAND));
    this.updateSpinnerConfig(true);
    this.scheduleService.commonPutMethod(`/status?isCollapse=${this.scheduleDetails().isCollapse}`, this.scheduleDetails().uuid).pipe(finalize(()=> this.updateSpinnerConfig(false))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.toasterService.success(DIALOG_MESSAGES.ROW_MESSAGE_UPDATED);
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  protected onSelectedTbRecord(data: TrialBalanceScheduleModel, groupIndex: number,rowIndex: number){
    this.scheduleDetails.update(schedule => {
      const group = schedule.schedules[groupIndex];
      group.rows[rowIndex] = {
        ...group.rows[rowIndex],
        accountName: data.description,
        accountNameChange: data.description,
        trialBalanceId: data.trialBalanceId,
        cyOriginalAmount: data.currYearValue,
        pyOriginalAmount: data.prevYearValue,
        isEdited: true
      };
      return {...schedule};
    });
    this.updateSubTotalValues();
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    this.spinnerConfig.update((config) => ({ ...config, isSpinShown: this.spinCount > 0 }));
  }

  protected getGroupByFilter(groupIndex: any){
    this.filteredTrialBalData().forEach(item1 => this.scheduleDetails().schedules[groupIndex].rows.some(item2 => item1.isHide = ((item1.trialBalanceId === item2.trialBalanceId) || item2.isDeleted)));
  }

  protected onEditMode(){
    if(!this.scheduleDetails().disabled) return;
    this.scheduleDetails.update(schedule=>{
      schedule.disabled = false;
      return {...schedule};
    });
  }

  private crossCheckTotal(){
    let total = {
      crossCheckCy: 0,
      crossCheckPy: 0
    }
    this.scheduleDetails().schedules.forEach((group, index) => {
      group.rows.forEach(row => {
        let originalRow = this.originalSchedules().schedules[index].rows.find(r => r.id === row.id);
        originalRow = structuredClone(originalRow ? originalRow : row);
        if(!(row.type === this.rowTypes.SUBTOTAL)){
          // CY
          if(row.cyOriginalAmountChange){
            if((!row.trialBalanceId && !row.isDeleted)){
              total.crossCheckCy = total.crossCheckCy + (- row.cyOriginalAmountChange);
            }else if(row.trialBalanceId && row.isDeleted){
              total.crossCheckCy = total.crossCheckCy + originalRow.cyOriginalAmount;
            } else if(row.trialBalanceId && !row.isDeleted){
              total.crossCheckCy = total.crossCheckCy + ((row.cyOriginalAmountChange - originalRow.cyOriginalAmount) * -1);
            }
          }else{
            if((!row.trialBalanceId && !row.isDeleted)){
              total.crossCheckCy = total.crossCheckCy + (- originalRow.cyOriginalAmount);
            }else if(row.trialBalanceId && row.isDeleted){
              total.crossCheckCy = total.crossCheckCy + originalRow.cyOriginalAmount;
            }
          }

          //   PY
          if(row.pyOriginalAmountChange){
            if((!row.trialBalanceId && !row.isDeleted)){
              total.crossCheckPy = total.crossCheckPy + (- row.pyOriginalAmountChange);
            }else if(row.trialBalanceId && row.isDeleted){
              total.crossCheckPy = total.crossCheckPy + originalRow.pyOriginalAmount;
            } else if(row.trialBalanceId && !row.isDeleted){
              total.crossCheckPy = total.crossCheckPy + ((row.pyOriginalAmountChange - originalRow.pyOriginalAmount) * -1);
            }
          }else{
            if((!row.trialBalanceId && !row.isDeleted)){
              total.crossCheckPy = total.crossCheckPy + (- originalRow.pyOriginalAmount);
            }else if(row.trialBalanceId && row.isDeleted){
              total.crossCheckPy = total.crossCheckPy + originalRow.pyOriginalAmount;
            }
          }
        }
      })
    });
    this.crossCheck.set(total);
  }
}


